import { type FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Input } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { formatDateTime } from '@/lib/format';
import { fieldError, type Problem, toProblem } from '@/lib/problem';
import { type MailSettings, settings } from './api';
import styles from './SettingsPage.module.css';

/**
 * What an administrator configures in the application itself (decision 6). The
 * SMTP transport and its credentials are deployment configuration; where mail goes
 * and whom it comes from are decided here, and recorded in the access trail.
 */
export function SettingsPage() {
  const [current, setCurrent] = useState<MailSettings | null>(null);
  const [team, setTeam] = useState('');
  const [sender, setSender] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  function show(loaded: MailSettings) {
    setCurrent(loaded);
    setTeam(loaded.teamMailbox ?? '');
    setSender(loaded.senderAddress ?? '');
  }

  useEffect(() => {
    let cancelled = false;
    settings
      .mail()
      .then((loaded) => {
        if (!cancelled) show(loaded);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    setSaved(false);
    try {
      show(await settings.saveMail(team.trim(), sender.trim()));
      setSaved(true);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Settings</h1>
      <section aria-labelledby="mail" className={styles.section}>
        <h2 id="mail" className={styles.sectionTitle}>Mail</h2>
        <p className={styles.lede}>
          The sender is the From address of everything the application sends, including the
          codes applicants sign in with: until it is set, applicants cannot sign in. With
          Amazon SES it must be a verified identity. The team mailbox is told of every
          submission.
        </p>
        {current && current.senderAddress === null && (
          <Alert tone="danger" title="No sender address">
            Applicants cannot sign in until one is set.
          </Alert>
        )}
        {problem && Object.keys(problem.fields).length === 0 && (
          <ProblemAlert problem={problem} title="Not saved" />
        )}
        {saved && <Alert tone="success" title="Saved">The change is recorded on the People page.</Alert>}
        <form onSubmit={(event) => void save(event)} className={styles.form} noValidate>
          <Input
            label="Sender address"
            type="email"
            value={sender}
            onChange={(event) => setSender(event.target.value)}
            helpText="For example, intake@your-firm.example"
            {...fieldError(problem, 'senderAddress')}
          />
          <Input
            label="Team mailbox"
            type="email"
            value={team}
            onChange={(event) => setTeam(event.target.value)}
            helpText="Receives a notice of each submission. Never the answers themselves."
            {...fieldError(problem, 'teamMailbox')}
          />
          <div>
            <Button type="submit" disabled={busy || current === null}>
              {busy ? 'Saving' : 'Save'}
            </Button>
          </div>
        </form>
        {current?.updatedBy && (
          <p className={styles.note}>
            Last changed by {current.updatedBy} on {formatDateTime(current.updatedAt)}.
          </p>
        )}
      </section>
    </div>
  );
}
